import React from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  List,
  ListItem,
  ListItemText,
  Divider,
  Button
} from '@mui/material';
import { Home } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useLanguageContext } from '../contexts/LanguageContext';

const TermsPageEN: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useLanguageContext();

  const handleGoHome = () => {
    navigate('/');
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Paper
        elevation={0}
        sx={{
          p: 4,
          borderRadius: 2,
          backgroundColor: 'transparent',
          color: 'white',
          '& .MuiTypography-root': { color: 'white' },
          '& .MuiAlert-root .MuiTypography-root': { color: 'inherit' },
          '& .MuiDivider-root': { borderColor: 'rgba(255, 255, 255, 0.3)' }
        }}
      >
        <Typography variant="h3" component="h1" gutterBottom align="center" color="primary">
          Terms of Service
        </Typography>
        
        <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 4 }}>
          Effective Date: January 10, 2025
        </Typography>

        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
          <Button
            variant="outlined"
            startIcon={<Home />}
            onClick={handleGoHome}
            sx={{
              borderRadius: 2,
              px: 3,
              py: 1,
              borderColor: 'white',
              color: 'white',
              '&:hover': {
                backgroundColor: 'white',
                color: '#6F4E37',
                borderColor: 'white'
              }
            }}
          >
            {t('terms.goHome')}
          </Button>
        </Box>

        <Divider sx={{ mb: 3 }} />

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom color="primary">
            Article 1 (Purpose)
          </Typography>
          <Typography variant="body1" paragraph>
            These Terms govern the rights, obligations, and responsibilities between Advanced Society Initiative (the "Company") and users regarding their use of the "He Listens to Me" service (the "Service").
          </Typography>
          <Typography variant="body1" paragraph>
            <strong>YouTube Service Agreement:</strong> By using this Service, users agree to be bound by the YouTube Terms of Service (https://www.youtube.com/t/terms).
          </Typography>
        </Box>

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom color="primary">
            Article 2 (Definitions)
          </Typography>
          <List>
            <ListItem>
              <ListItemText 
                primary="1. Service"
                secondary="All online services related to prayer and worship provided by the Company."
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="2. User"
                secondary="An individual who uses the Service provided by the Company in accordance with these Terms."
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="3. Content"
                secondary="All information, text, images, audio, etc. provided within the Service."
              />
            </ListItem>
          </List>
        </Box>

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom color="primary">
            Article 3 (Overview and Characteristics)
          </Typography>
          <Typography variant="body1" paragraph>
            The Service has the following characteristics:
          </Typography>
          <List>
            <ListItem>
              <ListItemText 
                primary="1. No Registration Required"
                secondary="Anyone can freely use the Service without a separate sign-up process."
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="2. Minimal Personal Information"
                secondary="Only minimal information is collected to protect the privacy of prayer content."
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="3. Temporary Data Processing"
                secondary="All information is automatically deleted after the worship service ends."
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="4. AI-personalized Worship"
                secondary="Prayer analysis, Bible verse recommendations, hymn/sermon composition, and benedictions are personalized by AI. Results are spiritual guidance, not legal or medical advice."
              />
            </ListItem>
          </List>
        </Box>

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom color="primary">
            Article 4 (Conditions of Use)
          </Typography>
          <List>
            <ListItem>
              <ListItemText 
                primary="1. Use by Minors"
                secondary="Minors must use the Service with the consent of their legal guardian."
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="2. Cautious Input"
                secondary="Users should exercise caution when entering sensitive prayer content. The Company aims to process only the minimum necessary for service provision."
              />
            </ListItem>
          </List>
        </Box>

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom color="primary">
            Article 5 (Provision of the Service)
          </Typography>
          <Typography variant="body1" paragraph>
            1. The Company, in principle, provides the Service 24 hours a day, 365 days a year.
          </Typography>
          <Typography variant="body1" paragraph>
            2. However, the Service may be restricted or suspended in whole or in part due to regular system maintenance, expansion or replacement, equipment failures, or other significant operational reasons.
          </Typography>
        </Box>

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom color="primary">
            Article 6 (Payments and Donations)
          </Typography>
          <Typography variant="body1" paragraph>
            Payments within the Service are voluntary donations by users. Payments follow the policies of payment processors (e.g., Toss Payments), and records are managed per the processor’s systems.
          </Typography>
        </Box>

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom color="primary">
            Article 7 (User Obligations)
          </Typography>
          <Typography variant="body1" paragraph>
            Users must not engage in the following:
          </Typography>
          <List>
            <ListItem>
              <ListItemText 
                primary="1. Identity Theft"
                secondary="Using others' personal information or entering false information"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="2. Service Disruption"
                secondary="Acts that may disrupt the stable operation of the Service"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="3. Illegal Activities"
                secondary="Acts that violate laws or public order and morals"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="4. Respect for the Sacred"
                secondary="Entering inappropriate content that harms the sanctity of prayer and worship"
              />
            </ListItem>
          </List>
        </Box>

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom color="primary">
            Article 8 (Privacy Protection)
          </Typography>
          <Typography variant="body1" paragraph>
            1. The Company endeavors to protect users' personal information in accordance with applicable laws.
          </Typography>
          <Typography variant="body1" paragraph>
            2. Gender information is used solely for worship preparation (scripture, benediction, etc.) and not for any other purpose.
          </Typography>
          <Typography variant="body1" paragraph>
            3. All information is automatically deleted after worship and is not stored separately.
          </Typography>
        </Box>

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom color="primary">
            Article 9 (Restriction of Service Use)
          </Typography>
          <Typography variant="body1" paragraph>
            If a user violates these Terms or interferes with the normal operation of the Service, the Company may restrict use of the Service in stages such as warnings, temporary suspension, or permanent suspension.
          </Typography>
        </Box>

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom color="primary">
            Article 10 (Intellectual Property)
          </Typography>
          <Typography variant="body1" paragraph>
            Intellectual property rights to the Service and its contents belong to the Company or relevant right holders. Users may use the content only for personal, non-commercial purposes and shall not reproduce, distribute, or create derivative works without authorization.
          </Typography>
        </Box>

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom color="primary">
            Article 11 (Disclaimer and Limitation of Liability)
          </Typography>
          <Typography variant="body1" paragraph>
            1. The Company shall not be liable for failure to provide the Service due to force majeure such as natural disasters.
          </Typography>
          <Typography variant="body1" paragraph>
            2. The Company is not responsible for Service disruptions caused by the user's fault.
          </Typography>
          <Typography variant="body1" paragraph>
            3. The Company is not responsible for users' expected profits or losses incurred from using the Service.
          </Typography>
        </Box>

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom color="primary">
            Article 12 (Dispute Resolution)
          </Typography>
          <Typography variant="body1" paragraph>
            1. The Company operates a compensation handling body to reflect legitimate opinions or complaints raised by users and to compensate for damages.
          </Typography>
          <Typography variant="body1" paragraph>
            2. Disputes arising in connection with these Terms shall be governed by the laws of the Republic of Korea, and the Seoul Central District Court shall have jurisdiction. 
          </Typography>
        </Box>

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom color="primary">
            Article 13 (Effect and Amendment of the Terms)
          </Typography>
          <Typography variant="body1" paragraph>
            1. These Terms apply to all users who wish to use the Service.
          </Typography>
          <Typography variant="body1" paragraph>
            2. The Company may amend these Terms to the extent that it does not violate relevant laws such as the Act on the Regulation of Terms and Conditions and the Act on Promotion of Information and Communications Network Utilization and Information Protection, etc.
          </Typography>
        </Box>

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom color="primary">
            Article 14 (Refund Policy)
          </Typography>
          <Typography variant="body1" paragraph>
            The Service's refund policy is as follows:
          </Typography>
          <List>
            <ListItem>
              <ListItemText
                primary="1. No Refunds After Digital Delivery"
                secondary="Once worship has begun or content (hymns/scripture/sermon/benediction, etc.) has been generated or provided, refunds are not available due to the nature of the Service. 'Provided' includes creation of the worship order, provision of YouTube/scripture/sermon links, or on-screen availability enabling substantive use."
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="2. Duplicate/Erroneous Payment"
                secondary="If duplicate or erroneous payments occur, contact the Company within 7 days from the payment date. After verification, a full or partial refund will be processed. Evidence (transaction history, payment ID, etc.) may be required."
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="3. Processing Time"
                secondary="Depending on the payment method and the payment processor’s policies (e.g., Toss Payments), refunds may take 3–7 business days. Additional time may be required for card issuer/bank processing."
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="4. Exceptions"
                secondary="Payments related to violations of these Terms or fraudulent use by the user are excluded from refunds. In cases of unauthorized/fraudulent transactions, the payment processor’s investigation results will apply."
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="5. Applicable Laws and Policies"
                secondary="Other matters related to refunds shall follow applicable laws and the policies of the relevant payment processor."
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="6. Donation Refunds"
                secondary="Users may request a refund of donations within 7 days from the payment date with a legitimate reason. Refunds may be limited if the donation has already been externally transferred or executed."
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="7. Refunds Due to System Malfunctions"
                secondary="If the use of the Service is materially affected due to a system malfunction, the Company will process a full or partial refund after verification following the payment processor’s policies."
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="8. How to Request a Refund"
                secondary="Send an email to huney.kong@gmail.com with: name, payment date/time, payment method, approval number/transaction ID, and reason for refund. You will receive an acknowledgment within 3 business days."
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="9. Partial Refunds and Fees"
                secondary="If the Service has been partially provided, a partial refund may be issued within a reasonable scope. If the user is at fault, payment processor fees may be deducted."
              />
            </ListItem>
          </List>
        </Box>

        <Divider sx={{ my: 3 }} />

        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <Button
            variant="contained"
            startIcon={<Home />}
            onClick={handleGoHome}
            sx={{
              mb: 3,
              borderRadius: 2,
              px: 4,
              py: 1.5,
              background: '#6F4E37',
              '&:hover': {
                background: '#5A3E2B',
                transform: 'translateY(-1px)',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)'
              },
              transition: 'all 0.3s ease'
            }}
          >
            {t('terms.goHome')}
          </Button>
          <Typography variant="body2" color="text.secondary">
            For inquiries, please contact Advanced Society Initiative (huney.kong@gmail.com).
          </Typography>
        </Box>
      </Paper>
    </Container>
  );
};

export default TermsPageEN;


